// Constants
import { Colors } from "@ms/uno-constants/lib/local/Colors";
// Styles
import { concatStyleSetsWithProps, FontSizes, FontWeights, memoizeFunction, mergeStyles } from "@fluentui/react";
import { highContrastLineInverted, highContrastSelectionBorder, highContrastWhiteOnBlack } from "../../../utilities/HighContrast.styles";
const notesMargin = "0 0 24px 0";
const notesVerticalPadding = "5px";
const notesLeftPadding = "7px";
const notesRightPadding = "6px";
const notesMaxHeight = "105px";
const notesGradient = "linear-gradient(rgba(255, 255, 255, 0), rgba(255, 255, 255, 1))";
const notesGradientHeight = "32px";
const ribbonContainerHeight = "44px";
export const richTextEditorWatermarkClassName = "editor-watermark";
/**
 * Helper to get styles for RichTextEditor
 * @param props Style props
 * @param customStyles Any custom style overrides
 */ export const getStyles = memoizeFunction((props, theme, customStyles)=>{
    const { readOnly, preview, isFocused, moreContent } = props;
    const root = {
        margin: notesMargin
    };
    const expandNotesButton = {
        fontSize: FontSizes.medium,
        fontWeight: FontWeights.semibold,
        marginTop: "4px",
        padding: "0",
        border: "none",
        backgroundColor: "transparent",
        color: theme.palette.themePrimary,
        cursor: "pointer"
    };
    const ribbonContainer = [
        {
            height: ribbonContainerHeight,
            maxWidth: "100%",
            visibility: "visible"
        }
    ];
    const editorWrapper = [
        {
            // Negative margin to align editor with edge of other controls
            marginLeft: `-${notesLeftPadding}`,
            marginRight: `-${notesRightPadding}`,
            boxSizing: "border-box",
            border: "1px solid transparent"
        },
        readOnly && [
            {
                color: theme.semanticColors.disabledText,
                backgroundColor: theme.semanticColors.disabledBackground
            },
            highContrastLineInverted
        ],
        preview && {
            maxHeight: notesMaxHeight,
            overflow: "hidden"
        },
        moreContent && {
            position: "relative",
            "::before": {
                content: "''",
                position: "absolute",
                left: 0,
                bottom: 0,
                right: 0,
                height: notesGradientHeight,
                backgroundImage: notesGradient,
                pointerEvents: "none"
            }
        },
        !readOnly && [
            !isFocused && {
                ":hover": {
                    border: `1px solid ${theme.semanticColors.inputBorder}`
                }
            },
            {
                [richTextEditorWatermarkClassName]: [
                    {
                        // Need to do this or else the !important will also override windows HC mode styles
                        "@media not screen and (-ms-high-contrast)": {
                            // WCAG AA compliant color contrast ratio of 4.6:1 against white background
                            // Changed from theme.palette.themePrimary (#aaaaaa) to meet accessibility standards
                            color: `${Colors.Grey46} !important`
                        }
                    },
                    highContrastWhiteOnBlack({
                        // Need to do this or else roosterjs's inline styles will override this color in windows HC mode
                        color: `${Colors.ConfettiGreen} !important`
                    })
                ]
            },
            isFocused && [
                {
                    border: `1px solid ${theme.palette.themePrimary}`,
                    ":hover": {
                        border: `1px solid ${theme.palette.themePrimary}`
                    }
                },
                highContrastSelectionBorder
            ]
        ]
    ];
    const notesEditor = [
        {
            /* padding: top right bottom left */ padding: `${notesVerticalPadding} ${notesRightPadding} ${notesVerticalPadding} ${notesLeftPadding}`,
            outline: "none",
            overflow: "hidden",
            overflowX: "auto",
            maxWidth: "100%",
            wordWrap: "break-word",
            // !important is necessary to override roosterjs's inline styles
            color: "inherit !important",
            backgroundColor: "inherit !important",
            selectors: {
                "> div > span": {
                    backgroundColor: "inherit !important",
                    color: `${theme.palette.neutralSecondary} !important`
                }
            }
        },
        readOnly && {
            // !important is necessary to override roosterjs's inline styles
            color: "inherit !important",
            backgroundColor: "inherit !important",
            cursor: "default",
            "> .div": {
                color: "inherit !important",
                backgroundColor: "inherit !important"
            }
        }
    ];
    const styles = {
        root,
        editorWrapper,
        expandNotesButton,
        notesEditor,
        ribbonContainer
    };
    return concatStyleSetsWithProps(props, styles, customStyles);
});
/**
 * Get the classnames associated with the computed styles
 * @param styles The styles for the component
 */ export const getClassNames = memoizeFunction((styles)=>({
        root: mergeStyles("editor-container", styles.root),
        editorWrapper: mergeStyles("editor-wrapper", styles.editorWrapper),
        expandNotesButton: mergeStyles("expandNotesButton", styles.expandNotesButton),
        notesEditor: mergeStyles("notes-editor", styles.notesEditor),
        ribbonContainer: mergeStyles("ribbon-container", styles.ribbonContainer)
    }));
